import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { LevelOverviewCard } from "../components/LevelOverviewCard";
import { api } from "../lib/api";
import {
  useActiveLanguageIsUnseeded,
  useLanguageCode,
} from "../lib/activeLanguage";
import { getLanguage } from "../lib/languages";
import { categoryText, cefrText, masteryBar } from "../lib/palette";
import { requireAuth } from "../lib/requireAuth";
import { UnseededLanguageLanding } from "../lib/UnseededLanguageLanding";

export const Route = createFileRoute("/home")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: HomePage,
});

// ProgressSnapshot and DailyProgress types were defined here for the
// old ProgressCard; the data still flows through progressQ via the
// generated API client's inferred return type, so the local types are
// no longer needed.

// CefrProgressCard moved to ../components/CefrProgressCard so it can
// be reused on /progress without duplication. CefrProgress type comes
// along with it. Importing both above.

// ProgressCard was the standalone "Today's practice" tile. Its data
// (attempts, retention gained, memory total) now lives inside the
// merged LevelOverviewCard. The last-7-days sparkline + the
// vs-yesterday delta were dropped — low signal-per-pixel for daily use.

function daysUntil(isoDate: string): number {
  const target = new Date(`${isoDate}T00:00:00`).getTime();
  return Math.max(0, Math.round((target - Date.now()) / 86_400_000));
}

const MONTH_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

function formatDeadlineLabel(isoDate: string, reason: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  return `${reason} · ${MONTH_YEAR.format(d)}`;
}

function DeadlineSection({
  deadline,
  mastered,
  total,
  recentWeeklyAttempts,
}: {
  deadline: { date: string; reason: string };
  mastered: number;
  total: number;
  // Sum of attempts over the last 7 days (from ProgressSnapshot.last7Days).
  // Used to compute pace headroom against the goal.
  recentWeeklyAttempts: number;
}) {
  const days = daysUntil(deadline.date);
  const pct = total > 0 ? Math.min(100, (mastered / total) * 100) : 0;

  // Pace synthesis (2026-05-23 Diego feedback): the deadline strip
  // told him `191 days · Goethe B1` but didn't answer the headline
  // question 'am I on track?'. We have the ingredients (skills
  // remaining, days remaining, last 7d attempt rate) — synthesize a
  // weekly target + an honest 'short / on / ahead' tag instead of
  // pretending a hard projection.
  const remaining = Math.max(0, total - mastered);
  const weeksRemaining = days > 0 ? days / 7 : 0;
  // How many skills/week would need to cross 70% for the learner to
  // hit the goal on time. Floor at 0.1/wk so the number stays
  // readable for a near-done learner.
  const requiredSkillsPerWeek =
    remaining > 0 && weeksRemaining > 0 ? remaining / weeksRemaining : 0;
  // Heuristic conversion: one skill crossing 70% takes ~12 quality
  // attempts (FSRS-5 reaches 70% retrievability after ~3 successful
  // reviews at ascending intervals on a fresh skill, and the
  // scheduler interleaves so the effective per-skill attempt budget
  // is wider). Tune as we get production telemetry; conservative is
  // better than triumphalist here.
  const ATTEMPTS_PER_SKILL_CROSS = 12;
  const requiredAttemptsPerWeek =
    requiredSkillsPerWeek * ATTEMPTS_PER_SKILL_CROSS;
  // Tag the pace: short if last-7d attempts < 75% of required, on
  // pace if within ±25%, ahead if > 125%. Bands are wide on purpose
  // — exact projection is dishonest, but bucketing is useful.
  let paceTone = "text-ink-400";
  let paceLabel: string;
  if (remaining <= 0) {
    paceLabel = "goal met";
    paceTone = "text-emerald-600";
  } else if (requiredAttemptsPerWeek <= 0) {
    paceLabel = "deadline passed";
    paceTone = "text-rose-600";
  } else {
    const ratio = recentWeeklyAttempts / requiredAttemptsPerWeek;
    if (ratio < 0.75) {
      paceLabel = "short of pace";
      paceTone = "text-rose-600";
    } else if (ratio <= 1.25) {
      paceLabel = "on pace";
      paceTone = "text-emerald-600";
    } else {
      paceLabel = "ahead of pace";
      paceTone = "text-emerald-600";
    }
  }

  return (
    <section className="mb-12 sm:mb-16">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h2 className="font-serif text-2xl text-ink-900">Deadline</h2>
        <div className="text-sm text-ink-400 tabular-nums">
          {days} days · {formatDeadlineLabel(deadline.date, deadline.reason)}
        </div>
      </div>
      <div className="w-full bg-ink-100 h-3 overflow-hidden">
        <div
          className="bg-accent h-3 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-ink-400 text-xs mt-2 tabular-nums">
        {mastered} / {total || "—"} skills above P 70% ({pct.toFixed(0)}%)
      </p>
      {remaining > 0 && weeksRemaining > 0 && (
        <p
          className="text-ink-400 text-xs mt-1 tabular-nums"
          title="Required pace assumes ~12 quality attempts per skill to push the model's memory probability above 70%. Last-7d attempt rate is your current pace. The on/short/ahead tag is heuristic, not a hard projection."
        >
          {remaining} left ·{" "}
          <span className="text-ink-700">
            {requiredSkillsPerWeek.toFixed(1)} skills/wk
          </span>{" "}
          (≈ {Math.round(requiredAttemptsPerWeek)} attempts) needed · last 7d{" "}
          {recentWeeklyAttempts} attempts ·{" "}
          <span className={paceTone}>{paceLabel}</span>
        </p>
      )}
    </section>
  );
}

type SkillRow = {
  id: number;
  code: string;
  name: string;
  category: string;
  retrievability: number;
  nAttempts: number;
  nCorrect: number;
  cefrLevel?: string | null;
};

function WeakestSkillsSection({ skills }: { skills: readonly SkillRow[] }) {
  return (
    <section className="mb-12 sm:mb-16">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h2 className="font-serif text-2xl text-ink-900">Weakest right now</h2>
        <Link to="/skills" className="text-sm text-ink-400 hover:text-ink-700">
          See all →
        </Link>
      </div>
      {skills.length === 0 ? (
        <p className="text-sm text-ink-400">
          Nothing's in the weak band right now — overdue reviews are your next
          move.
        </p>
      ) : (
        <ul className="space-y-2">
          {skills.map((s) => {
            const pct = Math.round(s.retrievability * 100);
            return (
              <li
                key={s.id}
                className="border border-ink-100 bg-paper px-3 sm:px-4 py-3 hover:border-ink-300 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <div className="min-w-0 flex items-center gap-2">
                    {s.cefrLevel && (
                      <span
                        className={`font-mono text-[10px] font-semibold ${cefrText(s.cefrLevel)} shrink-0`}
                        title={`CEFR level ${s.cefrLevel}`}
                      >
                        {s.cefrLevel}
                      </span>
                    )}
                    <span
                      className={`text-[10px] uppercase tracking-wide font-medium ${categoryText(s.category)} shrink-0`}
                      title="Skill kind"
                    >
                      {labelSkillKind(s.category)}
                    </span>
                    <span className="text-ink-900 text-sm sm:text-base truncate">
                      {s.name}
                    </span>
                  </div>
                  <span className="text-xs text-ink-400 tabular-nums shrink-0">
                    {pct}%
                  </span>
                </div>
                <div className="bg-ink-100 h-1.5 overflow-hidden">
                  <div
                    className={`h-1.5 transition-all ${masteryBar(s.retrievability)}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const WEAK_RECALL_THRESHOLD = 0.8;

function labelSkillKind(category: string): string {
  const map: Record<string, string> = {
    gram: "Grammar",
    vocab: "Vocab",
    phono: "Sound",
    morpho: "Form",
    pragma: "Use",
  };
  return map[category] ?? category;
}

function HomePage() {
  // Active-language pinning. Every query keys on `language` so a
  // switch in the nav triggers a refetch instead of showing stale
  // German data. `queryClient.clear()` in the switcher means there
  // is nothing to invalidate explicitly here.
  const language = useLanguageCode();
  const unseeded = useActiveLanguageIsUnseeded();
  if (unseeded) return <UnseededLanguageLanding />;

  const statsQ = useQuery({
    queryKey: ["vocabulary-stats", language],
    queryFn: async () => {
      const { data, error } = await api.GET("/stats/vocabulary", {
        params: { query: { language } },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });

  const progressQ = useQuery({
    queryKey: ["progress-stats", language],
    queryFn: async () => {
      const { data, error } = await api.GET("/stats/progress", {
        params: { query: { language } },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });

  // Drives the Deadline progress bar AND the "weakest skills" list
  // below. The list view at /skills uses the same query so React
  // Query dedupes the request.
  const skillsQ = useQuery({
    queryKey: ["skills-mastery", language],
    queryFn: async () => {
      const { data, error } = await api.GET("/skills/mastery", {
        params: { query: { language } },
      });
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });

  // Per-language metadata for hero copy + optional deadline. Falls
  // back to the registry's German entry if (for some reason) the
  // active code isn't recognised — keeps the home page rendering
  // rather than crashing.
  const meta = getLanguage(language) ?? getLanguage("de")!;
  const deadline = meta.deadline;

  return (
    <div className="mx-auto max-w-3xl lg:max-w-5xl px-4 sm:px-6 py-8 sm:py-12">
      <section className="mb-12 sm:mb-16">
        {/* Editorial kicker (§3) — a masthead line above the wordmark.
            "phase 1" names where this build is in the roadmap; the
            language slot makes the page recognisably localised at a
            glance once Italian/Portuguese ship. */}
        {/* Coordinate strip — schematic status label in the instrument
            style: a diamond tick, then phase / language / target / date. */}
        <div className="eyebrow mb-5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-accent2" aria-hidden>
            ◇
          </span>
          <span className="text-ink-700">Phase 1</span>
          <span>{meta.englishName.toUpperCase()}</span>
          {meta.deadline && (
            <>
              <span aria-hidden className="text-ink-300">
                —
              </span>
              <span>Target · B1</span>
              <span className="tabular-nums">
                {MONTH_YEAR.format(new Date(`${meta.deadline.date}T00:00:00`))}
              </span>
            </>
          )}
        </div>
        <h1 className="font-serif text-5xl sm:text-7xl font-semibold text-ink-900 mb-3 tracking-tight leading-[0.95]">
          lingua
        </h1>
        <p className="text-ink-500 text-base sm:text-lg mb-6 sm:mb-8 max-w-xl">
          {meta.tagline}
        </p>
        <div className="hairline mb-6 sm:mb-8" />
        <div className="flex flex-wrap gap-3">
          <Link
            to="/practice"
            preload="intent"
            className="group inline-flex items-center gap-2 bg-accent px-5 py-3 text-accent-fg hover:bg-accent-hover transition-colors"
          >
            <span>Start practice</span>
            <span
              aria-hidden
              className="text-ink-300 transition-transform duration-200 group-hover:translate-x-0.5"
            >
              →
            </span>
          </Link>
          <Link
            to="/skills"
            className="inline-flex items-center gap-2 border border-ink-200 px-5 py-3 text-ink-700 hover:border-ink-700 hover:text-ink-900 transition-colors"
          >
            <span>Browse skills</span>
          </Link>
        </div>
      </section>

      {/* One tile for the whole "where I am" story — today's activity
          + memory total + CEFR aggregate + confidence + vocabulary
          across the header strip, the three calibrated band bars
          (A1/A2/B1) as the body. Replaces the old ProgressCard +
          CefrProgressCard + CefrBandBreakdown + VocabularyCard stack. */}
      {skillsQ.data && skillsQ.data.length > 0 && (
        <LevelOverviewCard
          language={language}
          skills={skillsQ.data}
          vocab={statsQ.data ?? null}
          daily={
            progressQ.data
              ? {
                  attemptsToday: progressQ.data.today?.attempts ?? 0,
                  correctToday: progressQ.data.today?.correct ?? 0,
                  retentionDaysGainedToday:
                    progressQ.data.today?.retentionDaysGained ?? 0,
                  memoryTotal: progressQ.data.totalRetrievabilityMass,
                }
              : null
          }
        />
      )}

      {deadline && (
        <DeadlineSection
          deadline={deadline}
          mastered={progressQ.data?.skillsAboveThreshold ?? 0}
          total={skillsQ.data?.length ?? 0}
          recentWeeklyAttempts={(progressQ.data?.last7Days ?? []).reduce(
            (sum, d) => sum + d.attempts,
            0,
          )}
        />
      )}

      <WeakestSkillsSection
        skills={(skillsQ.data ?? [])
          .filter(
            (s) => s.nAttempts > 0 && s.retrievability < WEAK_RECALL_THRESHOLD,
          )
          .sort((a, b) => a.retrievability - b.retrievability)
          .slice(0, 6)}
      />

      {/* The full FSRS/IRT/scheduler/contrastive explanation lives at
          /about. The home page is for the daily-driver loop (queue,
          progress, weakest skills) — keeping a tease here is fine,
          but the four-section duplicate-of-About was eating the
          fold below it. */}
      <section className="border-t border-ink-100 pt-6">
        <p className="text-sm text-ink-400">
          <Link to="/about" className="hover:text-ink-700">
            How the adaptive engine works →
          </Link>
        </p>
      </section>
    </div>
  );
}
